import { MenuItem, useDisclosure } from "@chakra-ui/react";
import { InvestorEntityEditDrawer } from "features/investor-entities/InvestorEntityEditDrawer";
import { useInvestorEntityRetrieveSuspense } from "gen/endpoints/investor-entity/investor-entity";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { localizeJurisdiction } from "shared/utils/AddressUtils";

export const InvestorEntityProfileBlock: React.FC<{
  investorEntityId: string;
}> = ({ investorEntityId }) => {
  const { t } = useTranslation(["enums", "investorEntity", "entity", "common"]);

  const { data: investorEntity, refetch } =
    useInvestorEntityRetrieveSuspense(investorEntityId);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const onCloseComplete = useCallback(async () => {
    onClose();
    await refetch();
  }, [onClose, refetch]);

  return (
    <ContentBlockGroup>
      <ContentBlock mb="24px">
        <ContentBlockHeader>
          <ContentBlockActions>
            <Restricted permission={PermissionTypeEnum.config}>
              <ContentBlockMenu>
                <MenuItem onClick={onOpen}>{t("common:edit")}</MenuItem>
              </ContentBlockMenu>
            </Restricted>
          </ContentBlockActions>
        </ContentBlockHeader>
        <ContentBlockBody variant="descriptions">
          <Descriptions>
            <Descriptions.Item label={t("entity:legalName")}>
              {investorEntity.legal_name}
            </Descriptions.Item>
            <Descriptions.Item label={t("entity:externalId")}>
              {investorEntity.external_id}
            </Descriptions.Item>
            <Descriptions.Item label={t("entity:jurisdiction")}>
              {localizeJurisdiction(t, investorEntity)}
            </Descriptions.Item>
            <Descriptions.Item label={t("investorEntity:isForeign")}>
              {investorEntity.metadata.is_foreign}
            </Descriptions.Item>
            <Descriptions.Item label={t("investorEntity:isTaxExempt")}>
              {investorEntity.metadata.is_tax_exempt}
            </Descriptions.Item>
            <Descriptions.Item label={t("investorEntity:erisaInvestor")}>
              {investorEntity.metadata.erisa_investor}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("investorEntity:affiliatedWithSponsor")}
            >
              {investorEntity.metadata.affiliated_with_sponsor}
            </Descriptions.Item>
            <Descriptions.Item label={t("investorEntity:isBankHoldingCompany")}>
              {investorEntity.metadata.is_bank_holding_company}
            </Descriptions.Item>
          </Descriptions>
        </ContentBlockBody>
        {isOpen && (
          <InvestorEntityEditDrawer
            onCloseComplete={onCloseComplete}
            investorEntity={investorEntity}
          />
        )}
      </ContentBlock>
    </ContentBlockGroup>
  );
};
